import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

const KUPPI_URL =
  "https://res.cloudinary.com/dt8emxboh/image/upload/v1761305001/emocm50thu3idxar2qe4.png";

const UrjabioLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        "https://api.livestockify.com/api/urjabio/login",
        { email, password },
      );

      const { token, userId, email: userEmail, userRole } = response.data;

      localStorage.setItem("token", token);
      localStorage.setItem("User_Id", userId);
      localStorage.setItem("email", userEmail);
      localStorage.setItem("userRole", userRole);

      const role = userRole?.toLowerCase();
      if (role === "admin" || role === "client") {
        navigate(
          role === "admin"
            ? "/urjabio/admin/dashboard"
            : "/urjabio-user/dashboard",
        );
        setTimeout(() => {
          toast.success("Login successful!", {
            id: "urjabio-login",
            duration: 2500,
          });
        }, 50);
      } else {
        toast.error("Unauthorized role.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Login failed. Please check your credentials.",
      );
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="urjabio-login relative isolate min-h-screen w-full overflow-hidden">
      <Toaster
        position="top-right"
        toastOptions={{ className: "rounded-xl", duration: 2500 }}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap');

        .urjabio-login {
          font-family: 'Outfit', sans-serif;
          --ub-green: #34c759;
          --ub-green-deep: #0a5c33;
          --ub-mint: #e8f8ee;
          --ub-ink: #143028;
          background:
            radial-gradient(ellipse 80% 60% at 10% 20%, rgba(52, 199, 89, 0.22), transparent 55%),
            radial-gradient(ellipse 70% 50% at 90% 10%, rgba(10, 92, 51, 0.14), transparent 50%),
            radial-gradient(ellipse 60% 45% at 70% 90%, rgba(52, 199, 89, 0.16), transparent 55%),
            linear-gradient(165deg, #f4fbf6 0%, #eef7f1 42%, #e7f3ec 100%);
        }

        .urjabio-login .blob {
          position: absolute;
          border-radius: 40% 60% 55% 45%;
          filter: blur(2px);
          opacity: 0.55;
          pointer-events: none;
          will-change: transform;
        }
        .urjabio-login .blob-a {
          width: 28rem;
          height: 28rem;
          top: -8rem;
          left: -6rem;
          background: radial-gradient(circle at 30% 30%, #7ee2a0, #34c759 55%, transparent 70%);
          animation: ub-drift-a 14s ease-in-out infinite;
        }
        .urjabio-login .blob-b {
          width: 22rem;
          height: 22rem;
          right: -4rem;
          top: 18%;
          background: radial-gradient(circle at 60% 40%, #a8e6bf, #0a5c33 60%, transparent 72%);
          animation: ub-drift-b 18s ease-in-out infinite;
        }
        .urjabio-login .blob-c {
          width: 18rem;
          height: 18rem;
          bottom: -5rem;
          left: 35%;
          background: radial-gradient(circle at 50% 50%, #c8f0d4, #2fa84f 50%, transparent 70%);
          animation: ub-drift-c 16s ease-in-out infinite;
        }

        .urjabio-login .grid-fade {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(10, 92, 51, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(10, 92, 51, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 75%);
          pointer-events: none;
        }

        .urjabio-login .panel {
          animation: ub-rise 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .urjabio-login .brand-block {
          animation: ub-rise 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.08s both;
        }
        .urjabio-login .field {
          animation: ub-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .urjabio-login .field:nth-of-type(1) { animation-delay: 0.18s; }
        .urjabio-login .field:nth-of-type(2) { animation-delay: 0.28s; }
        .urjabio-login .field-btn { animation: ub-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) 0.38s both; }

        .urjabio-login .logo-float {
          animation: ub-float 5.5s ease-in-out infinite;
        }

        .urjabio-login .cta {
          position: relative;
          overflow: hidden;
        }
        .urjabio-login .cta::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.28) 50%, transparent 60%);
          transform: translateX(-120%);
          animation: ub-sheen 4.5s ease-in-out infinite;
        }

        .urjabio-login .display {
          font-family: 'Fraunces', Georgia, serif;
        }

        @keyframes ub-drift-a {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(36px, 28px) rotate(8deg); }
        }
        @keyframes ub-drift-b {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-30px, 40px) rotate(-6deg); }
        }
        @keyframes ub-drift-c {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(24px, -20px) scale(1.08); }
        }
        @keyframes ub-rise {
          from { opacity: 0; transform: translateY(22px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes ub-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes ub-sheen {
          0%, 70% { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .urjabio-login .blob,
          .urjabio-login .logo-float,
          .urjabio-login .cta::after,
          .urjabio-login .panel,
          .urjabio-login .brand-block,
          .urjabio-login .field,
          .urjabio-login .field-btn {
            animation: none !important;
          }
        }
      `}</style>

      <div className="blob blob-a" aria-hidden />
      <div className="blob blob-b" aria-hidden />
      <div className="blob blob-c" aria-hidden />
      <div className="grid-fade" aria-hidden />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-start px-4 pt-28 pb-10 sm:px-6 sm:pt-28 md:pt-28">
        <div className="brand-block mb-5 flex w-full max-w-md flex-col items-center text-center sm:mb-6">
          <img
            src={KUPPI_URL}
            alt="KuppiSmart"
            className="logo-float mb-4 h-20 w-auto object-contain sm:h-24 md:h-28"
          />
          <p className="display text-2xl font-semibold tracking-tight text-[var(--ub-ink)] sm:text-3xl">
            Energy, monitored live
          </p>
          <p className="mt-2 max-w-sm text-sm text-[var(--ub-green-deep)]/75 sm:text-[15px]">
            Sign in to your Urjabio workspace for real-time meter insights.
          </p>
        </div>

        <div className="panel w-full max-w-md rounded-2xl border border-white/70 bg-white/80 p-6 shadow-[0_20px_50px_-28px_rgba(10,92,51,0.45)] backdrop-blur-md sm:p-8">
          <h1 className="mb-6 text-center text-xl font-semibold tracking-tight text-[var(--ub-green-deep)] sm:text-2xl">
            Welcome back
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="field">
              <label
                htmlFor="urjabio-email"
                className="mb-1.5 block text-left text-xs font-semibold uppercase tracking-[0.12em] text-[var(--ub-green-deep)]/70"
              >
                Email
              </label>
              <input
                id="urjabio-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
                className="w-full rounded-xl border border-emerald-900/10 bg-[#f7fbf8] px-4 py-3.5 text-[15px] text-[var(--ub-ink)] outline-none transition placeholder:text-slate-400 focus:border-[var(--ub-green)] focus:bg-white focus:ring-2 focus:ring-[var(--ub-green)]/25"
              />
            </div>

            <div className="field">
              <label
                htmlFor="urjabio-password"
                className="mb-1.5 block text-left text-xs font-semibold uppercase tracking-[0.12em] text-[var(--ub-green-deep)]/70"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="urjabio-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-emerald-900/10 bg-[#f7fbf8] px-4 py-3.5 pr-12 text-[15px] text-[var(--ub-ink)] outline-none transition placeholder:text-slate-400 focus:border-[var(--ub-green)] focus:bg-white focus:ring-2 focus:ring-[var(--ub-green)]/25"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition hover:bg-emerald-50 hover:text-[var(--ub-green-deep)]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="field-btn pt-2">
              <button
                type="submit"
                disabled={loading}
                className={`cta inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-base font-semibold text-white transition ${
                  loading
                    ? "cursor-not-allowed bg-[var(--ub-green)]/60"
                    : "bg-[var(--ub-green-deep)] hover:bg-[#084a29] active:scale-[0.99]"
                }`}
              >
                {loading ? "Signing in…" : "Sign in"}
                {!loading && <ArrowRight size={18} strokeWidth={2.25} />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UrjabioLogin;
